import type { ProductKey } from '@/clients/modules';

export type { ProductKey } from '@/clients/modules';

export interface UpsellFeature {
    /** Path (under /public) to the feature's light-mode illustration SVG. Multi-colour brand artwork. */
    iconSrc: string;
    /** Dark-mode variant of the illustration (light strokes for dark backgrounds). */
    iconSrcDark: string;
    /** Short benefit heading, e.g. "Accurate". */
    title: string;
    body: string;
}

export interface UpsellProductConfig {
    /** Branded short name used outside the dialog (e.g. the ProductGuard lock screen), e.g. "SAVI™". */
    name: string;
    /** Bold uppercase headline shown at the top of the dialog (CSS upper-cases it). */
    title: string;
    /**
     * Hero image path (served from /public). Optional — the layout adapts to a single
     * content column when a product has no image yet.
     */
    image?: string;
    /**
     * Wordmark overlaid on the bottom half of the hero image. Only rendered when `image` is set.
     * Class strings must stay literal so Tailwind's scanner generates the arbitrary values.
     */
    logo?: {
        /** Path (under /public) to the wordmark SVG. */
        src: string;
        /** Fixed logo box, e.g. 'h-[72px] w-[198px]'. object-contain preserves aspect within it. */
        sizeClassName: string;
        /** Desktop-only positional nudge for the composition. */
        desktopClassName?: string;
    };
    /**
     * Decorative graphic overlaid on the hero (e.g. a location marker). Positioned absolutely
     * within the image pane; only rendered when `image` is set.
     */
    marker?: {
        src: string;
        /** Base (mobile-first) absolute positioning + size, e.g. 'absolute left-[10%] top-[15%] w-[61px]'. */
        className: string;
        /** Desktop additions/overrides, merged via cn so later classes win. */
        desktopClassName?: string;
    };
    /** Strong intro paragraph shown beneath the title. */
    intro: string;
    /** Icon-led benefit rows. May be empty for products without finalised copy. */
    features: UpsellFeature[];
    ctaLabel: string;
}

/**
 * Per-product upsell content. The dialog layout is product-agnostic and reads everything from here,
 * so adding/adjusting a product is a content change rather than a layout change.
 */
export const UPSELL_PRODUCTS: Record<ProductKey, UpsellProductConfig> = {
    savi: {
        name: 'SAVI™',
        title: 'Upgrade to Origin SAVI™',
        // Optimised WebP from /public — do NOT swap for the original large background image.
        image: '/savi-upsell-optimized.webp',
        logo: {
            src: '/savi-logo.svg',
            sizeClassName: 'h-[72px] w-[198px]',
            desktopClassName: 'translate-y-[50px]',
        },
        intro: 'Prevent contamination & streamline inspection with real-time, satellite insights that flag high-risk air valves',
        features: [
            {
                iconSrc: '/savi-feature-accurate.svg',
                iconSrcDark: '/savi-feature-accurate-dark.svg',
                title: 'Accurate',
                body: 'Considers topographical and weather data, soil type, valve location and more.',
            },
            {
                iconSrc: '/savi-feature-automated.svg',
                iconSrcDark: '/savi-feature-automated-dark.svg',
                title: 'Automated',
                body: 'Proprietary AI reduces the need for intensive traditional manual inspection',
            },
            {
                iconSrc: '/savi-feature-effective.svg',
                iconSrcDark: '/savi-feature-effective-dark.svg',
                title: 'Effective',
                body: 'Survey and monitor a whole air valve network with one subscription.',
            },
        ],
        ctaLabel: 'Upgrade Now',
    },
    'find-and-fix': {
        name: 'Find & Fix®',
        title: 'Upgrade to Origin Find & Fix®',
        // Optimised WebP from /public — do NOT swap for the original large designer export.
        image: '/find-and-fix-upsell-optimized.webp',
        logo: {
            src: '/find-and-fix-logo.svg',
            sizeClassName: 'h-[88px] w-[198px]',
            desktopClassName: 'translate-y-[50px]',
        },
        marker: {
            src: '/find-and-fix-marker.svg',
            // Sits low over the street, roughly level with the front of the van.
            className: 'absolute left-[6%] top-[45%] w-[61px]',
            desktopClassName: 'top-[50%] w-[122px]',
        },
        // Copy is verbatim from the supplied design — do not edit wording, punctuation or ® symbols.
        intro: 'Origin Find and Fix® is the only service that detects and repairs leaks in one visit — often in just 80 minutes',
        features: [
            {
                iconSrc: '/find-and-fix-feature-fast.svg',
                iconSrcDark: '/find-and-fix-feature-fast-dark.svg',
                title: 'Fast',
                body: 'We can identify a leak using the Origin Orbit® software and deploy a team to it to repair same day.',
            },
            {
                iconSrc: '/find-and-fix-feature-find-unknown-leaks.svg',
                iconSrcDark: '/find-and-fix-feature-find-unknown-leaks-dark.svg',
                title: 'Find Unknown Leaks',
                body: 'Origin Orbit® locates leaks, even leaks the water network are not aware of.',
            },
            {
                iconSrc: '/find-and-fix-feature-environmentally-friendly.svg',
                iconSrcDark: '/find-and-fix-feature-environmentally-friendly-dark.svg',
                title: 'Environmentally Friendly',
                body: 'The simplicity of the process means a huge reduction in carbon pollution for every repair undertaken.',
            },
        ],
        ctaLabel: 'Upgrade Now',
    },
};
